const clipboardToIE11Formatting = {
  "text/plain": "Text",
  "text/html": "Url",
  default: "Text",
};
let defaultMessage = "Copy to clipboard: #{key}, Enter";

const format = (message) => {
  const copyKey = (/mac os x/i.test(navigator.userAgent) ? "⌘" : "Ctrl") + "+C";
  return message.replace(/#{\s*key\s*}/g, copyKey);
};

export const COPY_FORMATS = {
  TEXT_PLAIN: "text/plain",
  TEXT_HTML: "text/html",
};

const deselectCurrent = function () {
  const selection = document.getSelection();
  if (!selection.rangeCount) {
    return function () {};
  }
  let active = document.activeElement;

  const ranges = [];
  for (let i = 0; i < selection.rangeCount; i++) {
    ranges.push(selection.getRangeAt(i));
  }

  switch (active.tagName.toUpperCase()) { // .toUpperCase handles XHTML
    case "INPUT":
    case "TEXTAREA":
      active.blur();
      break;

    default:
      active = null;
      break;
  }

  selection.removeAllRanges();
  return function () {
    selection.type === "Caret" &&
    selection.removeAllRanges();

    if (!selection.rangeCount) {
      ranges.forEach(function (range) {
        selection.addRange(range);
      });
    }

    active &&
    active.focus();
  };
};

export const copyText = (text, options) => {
  return new Promise((resolve) => {
    copy(text, {
      ...options,
      format: (options && options.format) || COPY_FORMATS.TEXT_PLAIN,
      onSuccess: () => resolve(true),
      onError: () => resolve(false),
    });
  });
};

const copyUsingCopyEvent = (e, options, text) => {
  try {
    e.stopPropagation();

    if (options.format) {
      e.preventDefault();

      if (typeof e.clipboardData === "undefined") {
        // IE 11
        console.warn("unable to use e.clipboardData");
        window.clipboardData.clearData();
        let format =
          clipboardToIE11Formatting[options.format] || clipboardToIE11Formatting["default"];
        window.clipboardData.setData(format, text);
      } else {
        // all other browsers
        e.clipboardData.clearData();
        if (options.format === COPY_FORMATS.TEXT_HTML) {
          if (options.textPlain) {
            e.clipboardData.setData(COPY_FORMATS.TEXT_PLAIN, options.textPlain);
          }
          e.clipboardData.setData(options.format, text);
        } else {
          e.clipboardData.setData(options.format, text);
        }
      }
    }

    if (options.onCopy) {
      e.preventDefault();
      options.onCopy(e.clipboardData);
    }

    return true;
  } catch (err) {
    console.log("Failed to copy using copyEvent': ", {
      err,
      event: e,
      options,
      text,
    });

    return false;
  }
};

// NOTE: Copying using a {document.execCommand} works on user interaction events,
// like a screen touch, but doesn't work on a "copy" event.
const copyUsingDocExec = (options, text) => {
  let reselectPrevious,
    range,
    selection,
    mark;

  try {
    reselectPrevious = deselectCurrent();

    range = document.createRange();
    selection = document.getSelection();

    mark = document.createElement("span");
    mark.textContent = text;
    // reset user styles for span element
    mark.style.all = "unset";
    // prevents scrolling to the end of the page
    mark.style.position = "fixed";
    mark.style.top = 0;
    mark.style.clip = "rect(0, 0, 0, 0)";
    // used to preserve spaces and line breaks
    mark.style.whiteSpace = "pre";
    // do not inherit user-select (it may be `none`)
    mark.style.webkitUserSelect = "text";
    mark.style.MozUserSelect = "text";
    mark.style.msUserSelect = "text";
    mark.style.userSelect = "text";
    mark.addEventListener("copy", (e) => copyUsingCopyEvent(e, options, text));

    document.body.appendChild(mark);

    range.selectNodeContents(mark);
    selection.addRange(range);

    return document.execCommand("copy");
  } catch (e) {
    console.log("Failed to copy using 'window.clipboardData': ", {
      hasDocExec: "execCommand" in document,
    });

    return false;
  } finally {
    if (selection) {
      if (typeof selection.removeRange == "function") {
        selection.removeRange(range);
      } else {
        selection.removeAllRanges();
      }
    }

    if (mark) {
      document.body.removeChild(mark);
    }

    reselectPrevious();
  }
};

const copyUsingClipboardData = (options, text) => {
  try {
    window.clipboardData.setData(options.format || "text", text);
    options.onCopy && options.onCopy(window.clipboardData);
    return true;
  } catch (err) {
    console.log("Failed to copy using 'window.clipboardData': ", {
      err,
      hasClipboardData: "clipboardData" in window,
    });

    return false;
  }
};

// Note: Is supported IOS greater than 13.
const copyUsingNavigatorClipboard = async (text) => {
  try {
    if ("navigator" in window && "clipboard" in window.navigator) {
      // Probably, need to implement html support.
      await window.navigator.clipboard.writeText(text);
    }

    return true;
  } catch (err) {
    console.error("Failed to copy using 'window.navigator.clipboard.writeText': ", {
      err,
      text,
      hasNavigatorClipboard: "navigator" in window && "clipboard" in window.navigator,
    });

    return false;
  }
};

export const copy = async (text, options) => {
  let success = false;

  if (!options) {
    options = {};
  }

  if (options.copyEvent) {
    success = copyUsingCopyEvent(options.copyEvent, options, text);
  }

  if (!success) {
    success = copyUsingDocExec(options, text);
  }

  if (!success) {
    success = copyUsingClipboardData(options, text);
  }

  if (!success) {
    success = await copyUsingNavigatorClipboard(text);
  }

  if (!success) {
    const message = format("message" in options ? options.message : defaultMessage);
    window.prompt(message, text);
  }

  if (success) {
    if (options.onSuccess) {
      options.onSuccess();
    }
  } else {
    if (options.onError) {
      options.onError();
    }
  }

  return success;
};
